import { format, parse, addDays, subDays, differenceInDays, isSameDay } from 'date-fns';

export const formatDate = (date, formatStr = 'MMM dd, yyyy') => {
  if (!date) return '';
  return format(new Date(date), formatStr);
};

export const formatTime = (time, formatStr = 'h:mm a') => {
  if (!time) return '';
  const parsed = parse(time, 'HH:mm', new Date());
  return format(parsed, formatStr);
};

const normalizeDateStr = (date) => {
  if (!date) return null;
  if (typeof date === 'string') return date.split('T')[0];
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'yyyy-MM-dd');
};

const normalizeTimeParts = (time) => {
  if (!time) return null;
  const parts = String(time).split(':');
  const h = Number(parts[0] || 0);
  const m = Number(parts[1] || 0);
  const s = Number(parts[2] || 0);
  if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return null;
  return { h, m, s };
};

const utcDateFromDateAndTime = (dateStr, timeStr) => {
  const dStr = normalizeDateStr(dateStr);
  const t = normalizeTimeParts(timeStr);
  if (!dStr || !t) return null;
  const [y, mo, da] = dStr.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, da, t.h, t.m, t.s));
};

export const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

export const formatUtcTimeToLocal = (timeStr, dateStr, locale = 'en-US') => {
  const dt = utcDateFromDateAndTime(dateStr, timeStr);
  if (!dt) return '';
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(dt);
};

export const formatUtcTimeRangeToLocal = (startTimeStr, endTimeStr, dateStr, locale = 'en-US') => {
  const start = formatUtcTimeToLocal(startTimeStr, dateStr, locale);
  const end = formatUtcTimeToLocal(endTimeStr, dateStr, locale);
  if (!start && !end) return '';
  if (!end) return start;
  if (!start) return end;
  return `${start} - ${end}`;
};

export const getLocalTimeHMFromUtc = (timeStr, dateStr) => {
  const dt = utcDateFromDateAndTime(dateStr, timeStr);
  if (!dt) return '';
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
};

export const isUtcSlotInPastForToday = (dateStr, startTimeStr) => {
  const dStr = normalizeDateStr(dateStr);
  if (!dStr) return false;
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  if (dStr !== todayStr) return false;
  const dt = utcDateFromDateAndTime(dStr, startTimeStr);
  if (!dt) return false;
  return dt.getTime() <= Date.now();
};

export const getDateRange = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate);
  while (currentDate <= new Date(endDate)) {
    dates.push(format(currentDate, 'yyyy-MM-dd'));
    currentDate = addDays(currentDate, 1);
  }
  return dates;
};

export const isPastDate = (date) => {
  return differenceInDays(new Date(), new Date(date)) > 0;
};

export const isToday = (date) => {
  return isSameDay(new Date(), new Date(date));
};

export const getWeekDates = (date = new Date()) => {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  const dates = [];
  for (let i = 0; i < 7; i++) {
    dates.push(format(addDays(start, i), 'yyyy-MM-dd'));
  }
  return dates;
};

export const getMonthDates = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  return getDateRange(firstDay, lastDay);
};
